import React, { useEffect, useRef, useState } from "react";
import axios from "../axiosConfig";
import {
  articleTitleFromSearchParams,
  deleteWikiSection,
  fetchWikiSection,
  setArticleSearchParam,
  wikiArticleShareUrl,
} from "../wikiSectionApi";
import ReactMarkdown from "react-markdown";      // ⬅️ new
import remarkGfm     from "remark-gfm";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import rehypeRaw from "rehype-raw";
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import CodeBlock from '@tiptap/extension-code-block';
import hljs from 'highlight.js';
import "highlight.js/styles/github.css";
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';

const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripHtmlTags = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const normalizeTextPasteToHtml = (rawText) => {
  const text = String(rawText || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\t/g, "    ");
  const lines = text.split("\n");

  const blocks = [];
  let listType = null; // "ul" | "ol" | null
  let listItems = [];

  const flushList = () => {
    if (!listType || listItems.length === 0) return;
    const tag = listType;
    blocks.push(
      `<${tag}>${listItems.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</${tag}>`
    );
    listType = null;
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }

    const unordered = trimmed.match(/^[-*•]\s+(.+)/);
    const ordered = trimmed.match(/^\d+[.)]\s+(.+)/);

    if (unordered) {
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listItems.push(unordered[1]);
      continue;
    }

    if (ordered) {
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listItems.push(ordered[1]);
      continue;
    }

    flushList();
    blocks.push(`<p>${escapeHtml(trimmed)}</p>`);
  }

  flushList();
  return blocks.join("");
};

export default function WikiEditor() {
  const [sections, setSections] = useState({});
  const [selectedTitle, setSelectedTitle] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    tags: [],
    text: "",
    newTag: "",
    mainSection: "General Topics",  // default selection
    subSection: ""                  // optional, start blank
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isFullIngesting, setIsFullIngesting] = useState(false);
  const [pasteAsPlainText, setPasteAsPlainText] = useState(false);
  const [message, setMessage] = useState("");
  const [tab, setTab] = useState("browse");
  const [linkCopied, setLinkCopied] = useState(false);

  const API_BASE = "/api"; // Replace with your backend IP
  
  const hasInitializedEditor = useRef(false);
  const loadedArticleParamRef = useRef(null);


  const refreshSections = async () => {
    try {
      const res = await axios.get(`${API_BASE}/full-sections`);
      const nested = {};
      for (const article of res.data) {
        const main = article.mainSection || "General Topics";
        const sub  = article.subSection  || "";
        if (!nested[main]) nested[main] = {};
        if (!nested[main][sub]) nested[main][sub] = [];
        nested[main][sub].push(article);
      }
      // Sort articles within each subsection
      for (const main in nested) {
        for (const sub in nested[main]) {
          nested[main][sub].sort((a, b) => a.title.localeCompare(b.title));
        }
      }
      // Sort sections and subsections alphabetically
      const sortedNested = Object.fromEntries(
        Object.entries(nested)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([main, subs]) => [
            main,
            Object.fromEntries(
              Object.entries(subs).sort(([a], [b]) => a.localeCompare(b))
            ),
          ])
      );
      setSections(sortedNested);
    } catch (e) {
      console.error("Failed to refresh sections", e);
    }
  };

  const handleSaveAndRebuild = async () => {
    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.tags.length ||
      !form.text.trim()
    ) {
      setMessage("❌ Please fill in Title, Description, at least one Tag, and Body Content before saving.");
      return;
    }

    setIsSaving(true);
    setMessage("💾 Saving article...");

    try {
      const saveResult = await handleSave();
      const isNewArticle = Boolean(saveResult?.new);
      await handleRebuild({ isNewArticle });
    } catch (err) {
      setMessage("❌ Save or rebuild failed.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Underline,
      CodeBlock.configure({
        HTMLAttributes: {
          class: 'hljs',
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: true,
        linkOnPaste: true,
        autolink: true,
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: "100%",
              parseHTML: element => element.getAttribute("width"),
              renderHTML: attributes => {
                if (!attributes.width) return {};
                return { width: attributes.width };
              },
            },
          };
        },
      }).configure({
        inline: false,
        allowBase64: false,
      }),
    ],
    content: form.text,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      const plain = editor.getText();

      setForm(prev => ({
        ...prev,
        text: html,
        searchText: plain
      }));
    }
  })

useEffect(() => {
  if (!editor?.view?.dom) return;

  const dom = editor.view.dom;

  const handlePaste = async (event) => {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const items = event.clipboardData?.items;
    if (!items || items.length === 0) return;

    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        event.preventDefault();      // ✅ block browser default base64 injection
        event.stopPropagation();     // ✅ prevent TipTap from interfering

        const file = item.getAsFile();
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
          const res = await axios.post(`${API_BASE}/upload-image/`, formData);
          const imageUrl = res.data.url;

          // ✅ Insert image into TipTap
          editor.chain().focus().setImage({ src: imageUrl, width: "100%" }).run();

          // ✅ Optional: Force update form state after image insertion
          setForm(prev => ({
            ...prev,
            text: editor.getHTML(),
            searchText: editor.getText()
          }));
        } catch (err) {
          console.error("❌ Failed to upload image:", err);
        }

        break; // only process the first image
      }
    }

    const html = clipboard.getData("text/html");
    const plain = clipboard.getData("text/plain");

    // Keep rich HTML behavior by default for better table fidelity.
    // Normalize only plain-text pastes or when user explicitly asks for plain text mode.
    if (pasteAsPlainText || (!html && plain)) {
      event.preventDefault();
      event.stopPropagation();

      const normalizedSource = pasteAsPlainText
        ? plain || stripHtmlTags(html)
        : plain;

      const normalizedHtml = normalizeTextPasteToHtml(normalizedSource);
      if (normalizedHtml.trim()) {
        editor.chain().focus().insertContent(normalizedHtml).run();
      }
    }
  };

  dom.addEventListener("paste", handlePaste);
  console.log("✅ Paste listener attached to editor DOM");

  return () => {
    dom.removeEventListener("paste", handlePaste);
  };
}, [editor?.view?.dom, pasteAsPlainText]);

    
  useEffect(() => {
    document.querySelectorAll('pre code').forEach(block => {
      hljs.highlightElement(block);
    });
  }, [form.text]);

  useEffect(() => {
    if (editor && !hasInitializedEditor.current && form.text) {
      editor.commands.setContent(form.text, false);
      hasInitializedEditor.current = true;
    }
  }, [editor, form.text]);


  useEffect(() => {
    refreshSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    
  const location = useLocation();
  const { docName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const articleParam = articleTitleFromSearchParams(searchParams);

useEffect(() => {
  // if someone arrives via /SomeDoc#SectionName, auto‐load that section
  if (docName && location.hash) {
    const section = decodeURIComponent(location.hash.slice(1));
    loadSection(section);
  }
}, [docName, location.hash]);

useEffect(() => {
  if (location.hash) {
    const id = decodeURIComponent(location.hash.slice(1));
    const tryScroll = (n = 20) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (n > 0) {
        setTimeout(() => tryScroll(n - 1), 100);
      }
    };
    tryScroll();
  }
}, [location.hash, form.text]);


  const loadSection = (title, { syncUrl = true } = {}) => {
    const cleanTitle = String(title || "").replace(/^🖋 |^📄 /, "").trim();
    if (!cleanTitle) return;
    hasInitializedEditor.current = false;
    if (syncUrl) {
      loadedArticleParamRef.current = cleanTitle;
      setArticleSearchParam(setSearchParams, cleanTitle);
    }
    fetchWikiSection(cleanTitle).then(res => {
      const chunk = res.data[0];
      setForm({
        title: title,
        description: chunk.description || "",
        tags: chunk.tags || [],
        text: chunk.text || "",
        newTag: "",
		    mainSection: chunk.mainSection || "General Topics",
        subSection:   chunk.subSection   || "",
        createdAt: chunk.createdAt || null,
        updatedAt: chunk.updatedAt || null,
        doc: chunk.doc || ""
      });
      setSelectedTitle(title);
      setTab("edit");
    });
  };

  // Open from shareable ?article= (and when the param changes).
  useEffect(() => {
    if (!articleParam) {
      loadedArticleParamRef.current = null;
      return;
    }
    if (loadedArticleParamRef.current === articleParam) return;
    loadedArticleParamRef.current = articleParam;
    loadSection(articleParam, { syncUrl: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleParam]);

  // Legacy in-app handoff from Q&A: rewrite location.state → ?article=
  useEffect(() => {
    const t = location.state?.openWikiTitle;
    if (typeof t === "string" && t.trim()) {
      setArticleSearchParam(setSearchParams, t.trim());
      navigate(".", { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const clearForm = () => {
    loadedArticleParamRef.current = null;
    setForm({
     title:       "",
     description: "",
     tags:        [],
     text:        "",
     newTag:      "",
     mainSection: "General Topics",
     subSection:  ""
    });
    setSelectedTitle("");
    setTab("edit");
	setMessage("");
    setArticleSearchParam(setSearchParams, null);
  };

  const copyEditorShareLink = async () => {
    const title = String(form.title || selectedTitle || "")
      .replace(/^🖋 |^📄 /, "")
      .trim();
    const url = wikiArticleShareUrl("/editor", title);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy article link:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

const currentDate = new Date().toISOString().split("T")[0];

const handleSave = async () => {
  const cleanTitle = form.title.replace(/^🖋 |^📄 /, "");
  const payload = {
  title: cleanTitle,
  text: form.text?.trim() || "",
  searchText: form.searchText?.trim() || "",  // 🧠 critical for QA
  source: `Manual Entry#${cleanTitle}`,
  doc: "Manual Entry",
  type: "wiki",
  mainSection: form.mainSection,
  subSection: form.subSection.trim() || "",
  description: form.description?.trim() || "",
  tags: form.tags || [],
  createdAt: form.createdAt || currentDate,
  updatedAt: currentDate
};

  try {
    const saveRes = await axios.post(`${API_BASE}/save-manual-entry`, payload);
    // Keep FE state aligned with BE identity semantics (title-based upsert).
    setSelectedTitle(cleanTitle);
    setForm((prev) => ({
      ...prev,
      title: cleanTitle,
      doc: "Manual Entry",
      createdAt: prev.createdAt || currentDate,
      updatedAt: currentDate,
    }));
    return saveRes.data;
  } catch (err) {
    console.error(err);
    setMessage("❌ Failed to save.");
    throw err;
  }
};


  const handleRebuild = async ({ isNewArticle = false } = {}) => {
    setIsRebuilding(true);
    setMessage(
      isNewArticle
        ? "⏳ Saved new article. Trying incremental rebuild; if alignment fails it will switch to FULL rebuild (this can take several minutes)..."
        : "⏳ Rebuilding index (incremental first). If incremental fails, it will switch to FULL rebuild..."
    );
    try {
      const result = await axios.post(`${API_BASE}/rebuild`);
      const backendMsg = result?.data?.message || "✅ Rebuild completed.";
      if (/Incremental misaligned/i.test(backendMsg)) {
        setMessage(
          "⚠️ Incremental rebuild failed and switched to FULL rebuild. Full rebuild completed successfully."
        );
      } else {
        setMessage(backendMsg);
      }
      await refreshSections();
    } catch (err) {
      setMessage("❌ Failed to rebuild index.");
      throw err;
    } finally {
      setIsRebuilding(false);
    }
  };

  const handleFullIngestion = async () => {
    setIsFullIngesting(true);
    setMessage("⏳ Running full ingestion for all wiki articles...");
    try {
      const result = await axios.post(`${API_BASE}/run-ingestion`);
      setMessage(result.data?.message || "✅ Full ingestion completed.");
      await refreshSections();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setMessage(`❌ Full ingestion failed: ${msg}`);
    } finally {
      setIsFullIngesting(false);
    }
  };

  const handleDelete = async () => {
    if (!form?.title) return;

    // Normalize icons/spacing and common Unicode punctuation mismatches
    const normalizeTitle = (s) =>
      (s || "")
        .replace(/^🖋\s|^📄\s/, "")           // leading icon from the list
        .replace(/\s+/g, " ")                // collapse whitespace
        .trim()
        .replace(/[\u2013\u2014]/g, "-")     // en/em dash -> hyphen
        .replace(/[\u2018\u2019]/g, "'");    // curly apostrophe -> straight

    const cleanTitle = normalizeTitle(form.title);

    const confirmed = window.confirm(
      `Are you sure you want to delete the section "${cleanTitle}"?`
    );
    if (!confirmed) return;

    setIsRebuilding(true);
    setMessage("🗑️ Deleting…");

    // Step 1: DELETE
    try {
      const { data } = await deleteWikiSection(cleanTitle);
      setMessage(data?.message || "✅ Deleted. Rebuilding…");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setMessage(`❌ Delete failed: ${msg}`);
      setIsRebuilding(false);
      return; // stop if delete failed
    }

    // Step 2: REBUILD (no ingestion here)
    try {
      await axios.post(`${API_BASE}/rebuild`);
      setMessage("✅ Deleted and rebuilt.");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setMessage(`⚠️ Deleted, but rebuild failed: ${msg}`);
    } finally {
      setIsRebuilding(false);
      clearForm();

      // If the URL had a #Section anchor, remove it so the app doesn't re-request the deleted section
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
      }

      // Optional: if you have a sections refresher, invoke it
      // (safe even if undefined)
      try {
        if (typeof refreshSections === "function") {
          await refreshSections();
        }
      } catch (_) {}
    }
  };

  const isInTable = editor?.isActive("table");

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
          Wiki editor
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          Create and maintain manual wiki content with the same writing and source quality used by Q&amp;A.
        </p>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3">
        <button
          className={`rounded border px-4 py-2 text-sm font-medium transition ${
            tab === "browse"
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          }`}
          onClick={() => setTab("browse")}
        >
          Browse & edit
        </button>
        <button
          className={`rounded border px-4 py-2 text-sm font-medium transition ${
            tab === "edit" && !selectedTitle
              ? "border-emerald-600 bg-emerald-600 text-white"
              : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          }`}
          onClick={clearForm}
        >
          New article
        </button>
        <button
          type="button"
          className={`rounded px-4 py-2 text-sm font-medium text-white ${
            isFullIngesting || isSaving || isRebuilding
              ? "bg-slate-500"
              : "bg-violet-600 hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-500"
          }`}
          onClick={handleFullIngestion}
          disabled={isFullIngesting || isSaving || isRebuilding}
        >
          {isFullIngesting ? "Running full ingestion..." : "Full ingestion"}
        </button>
      </div>

      {tab === "browse" && (
        <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Sections</h2>
          <ul className="list-disc space-y-1 pl-4 text-slate-800 dark:text-slate-200">
            {Object.entries(sections).map(([main, subs]) => (
              <li key={main}>
                <details>
                  <summary className="cursor-pointer font-semibold">{main}</summary>
                  <ul className="pl-4 list-disc">
                    {Object.entries(subs)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([sub, articles]) => (
                      <li key={sub}>
                        <details>
                          <summary>{sub || "General"}</summary>
                          <ul className="pl-4 list-disc space-y-1">
                            {articles.map((a) => (
                              <li key={a.title}>
                                <button
                                  className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                  onClick={() => loadSection(a.title)}
                                >
                                  {a.title}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        </div>
		
	  
	   
      )}

		{tab === "edit" && (
		  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80 sm:p-8">
			{/* ▼ Main & Sub-Section selectors ▼ */}
			<div className="mb-6">
			  <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">Main section</label>
			  <select
				className="w-full rounded border border-slate-300 bg-white p-3 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
				value={form.mainSection}
				onChange={e =>
				  setForm(prev => ({ ...prev, mainSection: e.target.value }))
				}
			  >
				<option>General Topics</option>
				<option>DDoS</option>
				<option>ADC</option>
				<option>Competitors</option>
				<option>Partners</option>
				<option>Cyber Controller</option>
				<option>WAAP</option>
				<option>SecureU</option>
			  </select>
			</div>

			<div className="mb-6">
        <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">Sub-section (optional)</label>
        <input
          type="text"
          className="mb-2 w-full rounded border border-slate-300 bg-white p-3 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          placeholder="Type a sub-section or leave blank"
          value={form.subSection}
          onChange={e =>
            setForm(prev => ({ ...prev, subSection: e.target.value }))
          }
        />
        {sections[form.mainSection] && (
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <span className="mb-1 block font-medium">Existing sub-sections:</span>
            <div className="flex flex-wrap gap-2">
              {Object.keys(sections[form.mainSection])
                .filter(sub => sub.trim() !== "")
                .sort()
                .map((sub, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      setForm(prev => ({ ...prev, subSection: sub }))
                    }
                    className="rounded bg-slate-200 px-2 py-1 text-sm text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                  >
                    {sub}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
			{/* ▲ End Main & Sub-Section selectors ▲ */}

			<div className="mb-6">
			  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
			    <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Title</label>
			    {selectedTitle && (
			      <button
			        type="button"
			        onClick={copyEditorShareLink}
			        className="rounded border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
			      >
			        {linkCopied ? "Copied!" : "Copy link"}
			      </button>
			    )}
			  </div>
			  <input
				name="title"
				className="w-full rounded border border-slate-300 bg-white p-3 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
				value={form.title}
				onChange={handleChange}
			  />
			</div>

      {(form.createdAt || form.updatedAt) && (
        <div className="mb-4 text-sm italic text-slate-500 dark:text-slate-400">
          {form.createdAt && (
            <div>Created on: {new Date(form.createdAt).toLocaleDateString()}</div>
          )}
          {form.updatedAt && form.updatedAt !== form.createdAt && (
            <div>Last updated: {new Date(form.updatedAt).toLocaleDateString()}</div>
          )}
        </div>
      )}

			<div className="mb-6">
			  <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">Description</label>
			  <textarea
				name="description"
				className="h-24 w-full resize-y rounded border border-slate-300 bg-white p-3 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
				value={form.description}
				onChange={handleChange}
			  />
			</div>

			<div className="mb-6">
			  <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">Tags</label>
			  <div className="flex flex-wrap gap-2">
				{form.tags.map((tag, index) => (
				  <span 
					key={index} 
					className="rounded-full bg-slate-200 px-2 py-1 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
				  >
					{tag}
					<button 
					  className="ml-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
					  onClick={() => {
						setForm(prev => ({
						  ...prev,
						  tags: prev.tags.filter((_, i) => i !== index)
						}));
					  }}
					>
					  ✖️
					</button>
				  </span>
				))}
			  </div>

			  <input
				type="text"
				name="newTag"
				value={form.newTag}
				placeholder="Add a tag..."
				onChange={(e) => setForm(prev => ({ ...prev, newTag: e.target.value }))}
				className="mt-2 w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
			  />
			  <button
				className="mt-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
				onClick={() => {
				  if (form.newTag.trim()) {
					setForm(prev => ({
					  ...prev,
					  tags: [...prev.tags, prev.newTag.trim()],
					  newTag: ""
					}));
				  }
				}}
			  >
				Add Tag
			  </button>
			</div>

      <div className="mb-6">
        <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">Body content</label>
       
       {editor && (
  <>
        {/* Toolbar */}
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Formatting
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/60">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-400"
                checked={pasteAsPlainText}
                onChange={(e) => setPasteAsPlainText(e.target.checked)}
              />
              Paste as plain text
            </label>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {pasteAsPlainText
                ? "Formatting is stripped and lists are normalized."
                : "Rich HTML paste enabled for better table fidelity."}
            </span>
          </div>
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("bold")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Bold
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("italic")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Italic
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("bulletList")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Bullet list
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("orderedList")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Numbered list
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("heading", { level: 2 })
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              H2
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("heading", { level: 3 })
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              H3
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("blockquote")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Quote
            </button>
            <button
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("code")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Inline code
            </button>
            <button
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              className={`rounded border px-3 py-1 text-sm ${
                editor.isActive("codeBlock")
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              Code block
            </button>
            <button
              onClick={() => {
                const url = prompt("Enter URL");
                if (url) {
                  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                }
              }}
              className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Link
            </button>
            <button
              onClick={() => editor.chain().focus().unsetLink().run()}
              className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Unlink
            </button>
          </div>

          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Tables
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
              className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Insert table
            </button>
            {isInTable && (
              <>
                <button
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Col left
                </button>
                <button
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Col right
                </button>
                <button
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Del col
                </button>
                <button
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Row above
                </button>
                <button
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Row below
                </button>
                <button
                  onClick={() => editor.chain().focus().deleteRow().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Del row
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Header row
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Header col
                </button>
                <button
                  onClick={() => editor.chain().focus().deleteTable().run()}
                  className="rounded border border-red-300 bg-red-50 px-3 py-1 text-sm text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/50"
                >
                  Delete table
                </button>
              </>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {isInTable
              ? "Table focus active: use Tab / Shift+Tab to move between cells."
              : "Click inside a table cell to unlock full table actions."}
          </p>
        </div>

        {/* Full-width editor */}
        <div className="rounded-lg border border-slate-300 bg-white p-4 dark:border-slate-600 dark:bg-slate-900">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Editing
          </p>
          <div className="min-h-[560px] rounded border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
            <EditorContent editor={editor} className="wiki-editor-content prose prose-slate max-w-none text-slate-900 dark:prose-invert dark:text-slate-100" />
          </div>
        </div>

        {/* Preview below editor */}
        <div className="mt-6 rounded-lg border border-slate-300 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-900/70">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Live preview
          </p>
          <div className="min-h-[420px] max-h-[760px] overflow-y-auto rounded border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950">
            <h2 id={form.title.trim()} className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">
              {form.title.trim() || "Untitled article"}
            </h2>
            <ReactMarkdown
              className="prose prose-slate max-w-none dark:prose-invert"
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ({ node, children, ...props }) => (
                  <a
                    {...props}
                    className="break-words text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                table: ({ node, ...props }) => (
                  <table
                    className="my-4 w-full border-collapse border border-slate-300 dark:border-slate-600"
                    {...props}
                  />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="border border-slate-300 bg-slate-100 px-3 py-2 text-left text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td
                    className="border border-slate-300 px-3 py-2 dark:border-slate-600"
                    {...props}
                  />
                ),
              }}
            >
              {form.text}
            </ReactMarkdown>
          </div>
        </div>
      </>
    )}
      </div>

			<div className="flex gap-6 flex-wrap">
        <button
          type="button"
          className={`rounded px-6 py-3 text-base font-medium text-white ${
            (isSaving || isRebuilding || isFullIngesting)
              ? 'bg-slate-500'
              : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500'
          }`}
          onClick={handleSaveAndRebuild}
          disabled={isSaving || isRebuilding || isFullIngesting}
        >
          {(isSaving || isRebuilding) ? "⏳ Please wait…" : "💾 Save"}
        </button>
			  {form.doc === "Manual Entry" && (
				<button
				  className="rounded bg-red-600 px-6 py-3 text-base font-medium text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500"
				  onClick={handleDelete}
          disabled={isFullIngesting}
				>
				  🗑️ Delete
				</button>
			  )}
			</div>

			{message && (
			  <p className="mt-6 text-sm font-semibold text-emerald-700 dark:text-emerald-400">{message}</p>
			)}
		  </div>
		)} {/* ← closes the tab === "edit" conditional */}

    </div>
  );
}
